import { useState } from 'react';
import { FileText } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@design-system';
import { usePolicyDetails } from '../hooks/usePolicyDetails';
import { FieldGrid, PerEnvTable } from './policyDetails/FieldGrid';
import { isEmpty } from './policyDetails/format';

interface PolicyDetailsPopoverProps {
  policy: PolicyFundingSource | null;
}

/**
 * Read-only summary of the policy (the same fields the input form renders for
 * this policy type) presented in a popover. Construction is delegated to
 * `usePolicyDetails`, so this component is purely presentational.
 */
export function PolicyDetailsPopover({ policy }: PolicyDetailsPopoverProps) {
  const [hideEmpty, setHideEmpty] = useState(true);
  const { identityFields, formFields, envRows, hasPerEnv, independentLivingZeroedOut } =
    usePolicyDetails({ policy });

  const visibleIdentity = hideEmpty ? identityFields.filter(f => !isEmpty(f.value)) : identityFields;
  const visibleFormFields = hideEmpty ? formFields.filter(f => !isEmpty(f.value)) : formFields;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <FileText className="h-4 w-4" />
          Policy details
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="max-h-[min(70vh,var(--radix-popover-content-available-height))] w-[480px] max-w-[90vw] overflow-auto"
      >
        <div className="mb-3 flex items-center justify-end">
          <label className="flex cursor-pointer items-center gap-2 text-xs text-gray-700">
            <input
              type="checkbox"
              checked={hideEmpty}
              onChange={e => setHideEmpty(e.target.checked)}
              className="h-3 w-3"
            />
            Hide empty fields
          </label>
        </div>
        <div className="space-y-4 [&_dd]:text-xs">
          {visibleIdentity.length > 0 && (
            <section>
              <h3 className="mb-1.5 border-b border-gray-200 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-700">
                Identity
              </h3>
              <FieldGrid fields={visibleIdentity} />
            </section>
          )}

          {visibleFormFields.length > 0 && (
            <section>
              <h3 className="mb-1.5 border-b border-gray-200 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-700">
                Policy inputs
              </h3>
              <FieldGrid fields={visibleFormFields} />
            </section>
          )}

          {hasPerEnv && (
            <section>
              <h3 className="mb-1.5 border-b border-gray-200 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-700">
                Daily benefit by care setting
              </h3>
              {independentLivingZeroedOut && (
                <Alert variant="warning" className="mb-2 p-2">
                  <AlertTitle className="text-xs">Independent living benefit not applied</AlertTitle>
                  <AlertDescription className="text-[11px] leading-snug">
                    Reimbursement policies do not cover independent living. The independent living
                    amount shown below is not used — projections pay $0 for this care setting.
                  </AlertDescription>
                </Alert>
              )}
              <PerEnvTable rows={envRows} />
            </section>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
